import axios from 'axios';
import DBService from './db';

class EpgParser {
    constructor() {
        this.channels = [];
        this.programs = [];
        this.progressCallback = null;
    }

    setProgressCallback(callback) {
        this.progressCallback = callback;
    }

    reportProgress(message, percent) {
        if (this.progressCallback) {
            this.progressCallback({ message, percent });
        }
    }

    async fetchAndParse(url, forceRefresh = false) {
        try {
            // Verificar cache primeiro
            if (!forceRefresh) {
                this.reportProgress('Verificando cache...', 5);
                const cachedData = await this.loadFromCache();
                if (cachedData) {
                    this.reportProgress('Carregando do cache...', 100);
                    return cachedData;
                }
            }

            this.reportProgress('Iniciando download do guia...', 10);

            // Tentar buscar via proxy se a URL direta falhar (CORS)
            let xmlData;
            try {
                const response = await axios.get(url, {
                    onDownloadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 10000000)); // Estimativa de 10MB se total desconhecido
                        // Mapear 0-100 do download para 10-80 do progresso total
                        const totalPercent = 10 + Math.round(percentCompleted * 0.7);
                        this.reportProgress(`Baixando guia: ${Math.round(progressEvent.loaded / 1024 / 1024)}MB`, totalPercent);
                    }
                });
                xmlData = response.data;
            } catch (error) {
                console.warn('Erro ao buscar EPG diretamente, tentando fallback...', error);
                this.reportProgress('Tentando método alternativo...', 20);
                const response = await axios.get('/epg.xml', {
                    onDownloadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 8000000));
                        const totalPercent = 20 + Math.round(percentCompleted * 0.6);
                        this.reportProgress(`Baixando guia local: ${Math.round(progressEvent.loaded / 1024 / 1024)}MB`, totalPercent);
                    }
                });
                xmlData = response.data;
            }

            this.reportProgress('Processando dados XML...', 85);
            // Dar um pequeno delay para a UI atualizar
            await new Promise(resolve => setTimeout(resolve, 100));

            const parsedData = this.parseXml(xmlData);

            this.reportProgress('Salvando no cache...', 95);
            await this.saveToCache(parsedData);

            this.reportProgress('Concluído!', 100);
            return parsedData;
        } catch (error) {
            console.error('Erro ao buscar/parsear EPG:', error);
            return { channels: [], programs: [] };
        }
    }

    async saveToCache(data) {
        try {
            this.reportProgress('Salvando no banco de dados...', 95);
            await DBService.saveEPG(data);
        } catch (e) {
            console.error('Erro ao salvar cache EPG', e);
        }
    }

    async loadFromCache() {
        return await DBService.loadEPG();
    }

    parseXml(xmlString) {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, "text/xml");

        // Parse Channels
        const channelNodes = xmlDoc.getElementsByTagName('channel');
        const channels = Array.from(channelNodes).map(node => {
            const id = node.getAttribute('id');
            const displayNameNode = node.getElementsByTagName('display-name')[0];
            const iconNode = node.getElementsByTagName('icon')[0];

            return {
                id: id,
                name: displayNameNode ? displayNameNode.textContent : id,
                logo: iconNode ? iconNode.getAttribute('src') : null
            };
        });

        // Parse Programs
        const programNodes = xmlDoc.getElementsByTagName('programme');
        const programsByChannel = {};

        // Otimização: Processar programas em um loop simples para evitar criação excessiva de objetos temporários
        // e filtrar programas muito antigos (mais de 24h atrás) para economizar espaço
        const now = Date.now();
        const ONE_DAY_MS = 24 * 60 * 60 * 1000;
        const cutoffTime = now - ONE_DAY_MS;

        for (let i = 0; i < programNodes.length; i++) {
            const node = programNodes[i];
            const stopStr = node.getAttribute('stop');
            const stopTime = this.parseDate(stopStr).getTime();

            // Ignorar programas que já terminaram há mais de 24h
            if (stopTime < cutoffTime) continue;

            const channelId = node.getAttribute('channel');
            const startStr = node.getAttribute('start');
            const titleNode = node.getElementsByTagName('title')[0];
            const descNode = node.getElementsByTagName('desc')[0];

            if (!programsByChannel[channelId]) {
                programsByChannel[channelId] = [];
            }

            programsByChannel[channelId].push({
                start: this.parseDate(startStr),
                stop: new Date(stopTime),
                title: titleNode ? titleNode.textContent : 'Sem Título',
                description: descNode ? descNode.textContent : ''
            });
        }

        // Ordenar programas por horário
        Object.keys(programsByChannel).forEach(channelId => {
            programsByChannel[channelId].sort((a, b) => a.start - b.start);
        });

        return {
            channels,
            programs: programsByChannel
        };
    }

    parseDate(dateStr) {
        // Formato XMLTV: YYYYMMDDHHMMSS +0000
        // Ex: 20251121030000 +0000
        if (!dateStr) return new Date();

        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6) - 1; // Mês começa em 0
        const day = dateStr.substring(6, 8);
        const hour = dateStr.substring(8, 10);
        const minute = dateStr.substring(10, 12);
        const second = dateStr.substring(12, 14);

        return new Date(Date.UTC(year, month, day, hour, minute, second));
    }
}

export default new EpgParser();
